import type { IClass } from '@/types/class';
import type { IAddClassPayload } from '@/types/payload';

import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { IconEdit, IconPlus } from '@tabler/icons-react';

import CustomModal from '@/components/CustomModal';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import AddClassDrawerSkeleton from '@/components/SkeletonLoader/AddClassDrawerSkeleton';
import { useFormatText } from '@/hooks/useFormatText';
import {
  addClassFormSchema,
  type TAddClassForm,
} from '@/schema/addClassSchema';
import { addClass, updateClass } from '@/service/classService';
import { getClassTeacherDropdown } from '@/service/teacherService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { zodResolver } from '@hookform/resolvers/zod';

interface IAddClassDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSuccess?: () => void;
  onClose?: () => void;
  classToEdit?: IClass | null;
}

interface ITeacherDropdownItem {
  id: string;
  name: string;
}

const AddClassDrawer = ({
  isOpen,
  setIsOpen,
  onSuccess,
  onClose,
  classToEdit,
}: IAddClassDrawerProps) => {
  const { formatText } = useFormatText();
  const { isDrawerLoading } = useSkeletonStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [teacherOptions, setTeacherOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<TAddClassForm>({
    resolver: zodResolver(addClassFormSchema),
    mode: 'onBlur',
    defaultValues: {
      className: '',
      assignedTeachers: [],
      classTeacher: '',
    },
  });

  const isEditMode = Boolean(classToEdit);

  // Populate form when editing — use data already available from the list
  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode && classToEdit) {
      reset({
        className: classToEdit.name || '',
        assignedTeachers: classToEdit.teacher_id || [],
        classTeacher: classToEdit.class_teacher_id || '',
      });
    } else {
      reset({
        className: '',
        assignedTeachers: [],
        classTeacher: '',
      });
    }
  }, [classToEdit, isOpen, isEditMode, reset]);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const res = await getClassTeacherDropdown(true);
        if (res?.status && Array.isArray(res.data)) {
          const options = (res.data as ITeacherDropdownItem[])
            .filter((teacher) => Boolean(teacher?.id && teacher?.name))
            .map((teacher) => ({
              value: teacher.id,
              label: teacher.name,
            }));
          setTeacherOptions(options);
        } else {
          setTeacherOptions([]);
          if (res?.message) {
            enqueueSnackbar(res.message, { variant: 'error' });
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    if (isOpen) {
      fetchTeachers();
    }
  }, [isOpen]);

  const handleClose = () => {
    reset();
    setIsOpen(false);
    onClose?.();
  };

  const onSubmit = async (data: TAddClassForm) => {
    setIsSubmitting(true);

    try {
      const payload: IAddClassPayload = {
        name: data.className,
        teacher_id: data.assignedTeachers || [],
        class_teacher_id: data.classTeacher || '',
      };

      let response;
      if (isEditMode && classToEdit?.id) {
        response = await updateClass(classToEdit.id, payload);
      } else {
        response = await addClass(payload);
      }

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        handleClose();
        onSuccess?.();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={handleClose}
      isHeader
      heading={formatText(
        isEditMode ? 'add-class.edit-title' : 'add-class.title'
      )}
      isFooter
      position="right"
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        variant: 'link',
        onClick: handleClose,
      }}
      confirmButtonProps={{
        label: formatText(
          isEditMode ? 'add-class.edit-submit' : 'add-class.submit'
        ),
        variant: 'primary',
        icon: isEditMode ? <IconEdit size={18} /> : <IconPlus size={18} />,
        onClick: handleSubmit(onSubmit),
        loading: isSubmitting || isDrawerLoading,
        disabled: isSubmitting || isDrawerLoading,
      }}
    >
      {isDrawerLoading ? (
        <AddClassDrawerSkeleton />
      ) : (
        <form
          className="flex flex-col gap-5 w-full"
          onSubmit={handleSubmit(onSubmit)}
          method="post"
        >
          {/* Class Name */}
          <Input
            name="className"
            label={formatText('add-class.fields.class-name')}
            placeholder={formatText('add-class.fields.class-name-placeholder')}
            register={register}
            type="text"
            required
            error={
              errors.className?.message
                ? formatText(errors.className.message)
                : undefined
            }
            dataTestId="class-name-input"
          />

          {/* Assign Teacher (Multi-select) */}
          <Controller
            name="assignedTeachers"
            control={control}
            render={({ field }) => (
              <Select
                name="assignedTeachers"
                options={teacherOptions}
                selected={field.value || []}
                onChange={(val) => field.onChange(val)}
                placeholder={formatText('add-class.fields.assign-teacher')}
                isMulti
                isClearable
                dataTestId="assign-teacher-select"
              />
            )}
          />

          {/* Assign Class Teacher (Single Select) */}
          <Controller
            name="classTeacher"
            control={control}
            render={({ field }) => (
              <Select
                name="classTeacher"
                options={teacherOptions}
                selected={field.value || ''}
                onChange={(val) =>
                  field.onChange(Array.isArray(val) ? val[0] || '' : val)
                }
                placeholder={formatText('add-class.fields.class-teacher')}
                isClearable
                isRequired
                error={
                  errors.classTeacher?.message
                    ? formatText(errors.classTeacher.message)
                    : undefined
                }
                dataTestId="class-teacher-select"
              />
            )}
          />
        </form>
      )}
    </CustomModal>
  );
};

export default AddClassDrawer;
